








































































































































































import moment from 'moment-timezone';
import { mapActions, mapGetters } from 'vuex';
import { merge, omit } from 'lodash';

import { ISystemError } from '@/models/error/error.types';
import { IContractFetchParams } from '@/services/contract';
import { setErrorFromAxiosError } from '@/utils/helpers';
import { ContractRenewalStateEnum, IContract } from '@/models';

import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';
import auth from '@/mixins/auth';
import SnackBarMixin from '@/mixins/snackbar';

import ALPage from '@/components/core/Page/ALPage.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ContractBuilderDialog from '@/components/contract/ContractBuilderDialog.vue';
import ContractDetailOptionsButton from '@/components/contract/ContractDetailOptionButton.vue';
import ContractRenewalDisplay from '@/components/contract/ContractRenewalDisplay.vue';
import ContractRenewalStateDisplay from '@/components/contract/ContractRenewalStateDisplay.vue';
import ContractPhaseDisplay from '@/components/contract/ContractPhaseDisplay.vue';
import ContractSearchFilter from '@/components/contract/ContractSearchFilter.vue';

const baseMixins = mixins(Functions, auth, SnackBarMixin);

interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    contactBuilderDialog: InstanceType<typeof ContractBuilderDialog>;
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'ContractListPage',

    components: {
      ALPage,
      ErrorAlert,
      ContractBuilderDialog,
      ContractDetailOptionsButton,
      ContractRenewalDisplay,
      ContractRenewalStateDisplay,
      ContractSearchFilter,
      ContractPhaseDisplay
    },
    data() {
      return {
        loading: false,
        isSearchFilterActive: false,

        error: null as ISystemError | null,
        params: {
          search: this.$route.query.search as string,
          page: Number(this.$route.query.page ?? 1)
        } as IContractFetchParams,

        sortBy: 'id',
        sortDesc: true,

        headers: [
          {
            text: '',
            sortable: false,
            value: 'flag',
            width: 20
          },
          {
            text: '#',
            sortable: false,
            value: 'contractId',
            width: 10
          },
          {
            text: 'Customer',
            sortable: false,
            value: 'quote.customer',
            width: 180
          },
          {
            text: 'Project',
            sortable: false,
            value: 'quote.project',
            width: 250
          },
          {
            text: 'Proposal',
            sortable: false,
            value: 'quote',
            width: 80
          },
          {
            text: 'Order #',
            sortable: false,
            value: 'csi_order_number',
            width: 80
          },
          {
            text: 'Amount',
            sortable: false,
            value: 'total_value',
            width: 150,
            align: 'end'
          },
          {
            text: 'Start Date',
            sortable: false,
            value: 'activation_date',
            width: 120,
            align: 'start'
          },
          {
            text: 'End Date',
            sortable: false,
            value: 'expiry_date',
            width: 120,
            align: 'start'
          },
          {
            text: 'Renewal Status',
            sortable: false,
            value: 'renewal_state',
            width: 100,
            align: 'center'
          },
          {
            text: 'Status',
            sortable: false,
            value: 'phase',
            width: 100,
            align: 'center'
          },
          {
            text: '',
            sortable: false,
            align: 'center',
            width: 100,
            value: 'actions'
          }
        ]
      };
    },

    computed: {
      ContractRenewalStateEnum: () => ContractRenewalStateEnum,

      ...mapGetters({
        contracts: 'contract/getContracts',
        numContracts: 'contract/getContractCount',
        getCurrencyById: 'currency/getCurrencyById'
      }),

      extraFiltersApplied(): boolean {
        return Object.keys(omit(this.$route.query, ['page', 'search'])).some(
          v => !!v
        );
      }
    },

    mounted() {
      this.loading = true;
      this.fetchCurrencies().catch(error => {
        this.error = setErrorFromAxiosError(error);
      });

      merge(this.params, omit(this.$route.query, ['page', 'search']));
      if (this.$route.query.renewal_required) {
        let renewalRequiredParam: boolean | null = null;
        if (this.$route.query.renewal_required === 'false') {
          renewalRequiredParam = false;
        } else if (this.$route.query.renewal_required === 'true') {
          renewalRequiredParam = true;
        }
        this.params.renewal_required = renewalRequiredParam;
      }

      this.searchContracts(this.params);
    },

    watch: {
      'params.search'(search: string) {
        this.debounceFetchContracts({
          ...this.params,
          search: search,
          page: 1
        }).then(() => {
          this.params.page = 1;
        });
      }
    },

    methods: {
      moment,

      ...mapActions({
        fetchContracts: 'contract/fetchContracts',
        fetchCurrencies: 'currency/fetchCurrencies'
      }),

      clearAllErrors(): void {
        this.error = null;
      },

      searchContracts(params: IContractFetchParams) {
        /* this will update the URL query based on param values. */
        this.$router
          .replace({
            name: 'contracts',
            query: params as Record<string, any>
          })
          .catch((e: Error) => e); //catch navigation duplication error

        return this.fetchContracts(params)
          .catch(error => {
            this.error = setErrorFromAxiosError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      onUpdatePage(newPage: number) {
        if (newPage != this.params.page) {
          this.searchContracts({
            ...this.params,
            page: newPage
          }).then(() => {
            this.params.page = newPage;
          });
        }
      },

      async debounceFetchContracts(params: IContractFetchParams) {
        this.clearAllErrors();
        this.loading = true;

        return this.debounce(this.searchContracts, 200)(params);
      },

      refreshSearchFilter(params: IContractFetchParams) {
        this.clearAllErrors();

        this.loading = true;
        this.searchContracts({
          page: 1,
          search: this.params.search,
          ...params
        }).then(() => {
          // update params object
          this.params = {
            ...omit(params, ['page']),
            page: 1,
            search: this.params.search
          };
        });
      },

      goToContractDetailPage(contract: IContract) {
        this.$router.push({
          name: 'contract-detail',
          params: {
            id: String(contract.id)
          }
        });
      },

      openContractWizard(contract?: IContract) {
        this.$refs.contactBuilderDialog.open(contract);
      }
    }
  });
